/**
 * The install control on the About page.
 *
 * All four states say something. An install button that quietly does nothing
 * because the browser never offered a prompt is the version of this that
 * wastes a person's afternoon, so where there is no prompt to fire the steps
 * are written out instead.
 */

import { useEffect, useState } from 'react';
import { installState, readInstallEnv } from '../lib/install.js';

/** Chrome's install prompt. Not in lib.dom, so it is described here. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function Install() {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    function capture(event: Event) {
      // Chrome shows its own bar unless the event is taken, and the whole
      // point of holding it is to put the offer where the explanation is.
      event.preventDefault();
      setPrompt(event as InstallPromptEvent);
    }
    function done() {
      setInstalled(true);
      setPrompt(null);
    }

    addEventListener('beforeinstallprompt', capture);
    addEventListener('appinstalled', done);
    return () => {
      removeEventListener('beforeinstallprompt', capture);
      removeEventListener('appinstalled', done);
    };
  }, []);

  const state = installed
    ? ({ kind: 'installed' } as const)
    : installState(readInstallEnv(prompt !== null));

  if (state.kind === 'installed') {
    return (
      <p className="options-note">
        Installed — you are running slate from your home screen.
      </p>
    );
  }

  if (state.kind === 'prompt') {
    return (
      <div className="install">
        <button
          className="action install-btn"
          onClick={async () => {
            const event = prompt;
            if (!event) return;
            // A prompt can only be fired once. Dropping it either way keeps
            // the button from becoming one that stops working silently.
            setPrompt(null);
            await event.prompt().catch(() => {});
          }}
        >
          Add slate to your home screen
        </button>
        <p className="options-note">
          It opens in its own window, with no browser bar. Nothing is downloaded
          and it does not work offline.
        </p>
      </div>
    );
  }

  if (state.kind === 'ios') {
    return (
      <div className="install">
        <ol className="install-steps">
          <li>
            Tap <strong>Share</strong> at the bottom of Safari.
          </li>
          <li>
            Choose <strong>Add to Home Screen</strong>.
          </li>
        </ol>
        <p className="options-note">
          It opens in its own window, with no browser bar. Safari is the only
          browser on iOS that can do this — Chrome and Firefox there cannot.
        </p>
      </div>
    );
  }

  return (
    <div className="install">
      <p className="options-note">
        Open your browser's menu and choose <strong>Install slate</strong>, or{' '}
        <strong>Add to Home screen</strong>. It opens in its own window, with no
        browser bar.
      </p>
    </div>
  );
}
